import { useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Mail, CheckCircle2 } from "lucide-react";

/**
 * Newsletter capture form — reusable footer/inline block.
 * Props:
 * - source: string ("blog" | "tuto" | "footer" | "landing")
 * - variant: "card" (default) | "inline"
 */
export default function NewsletterForm({ source = "footer", variant = "card" }) {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) {
      toast.error("Email invalide");
      return;
    }
    setLoading(true);
    try {
      const r = await api.post("/newsletter/subscribe", { email: email.toLowerCase().trim(), source });
      setDone(true);
      if (r.data.status === "already_subscribed") {
        toast.info("Vous êtes déjà inscrit — merci !");
      } else {
        toast.success("Merci ! Vous recevrez notre prochaine newsletter.");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'inscription");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div
        className={
          variant === "card"
            ? "rounded-2xl border-2 border-emerald-200 bg-emerald-50 p-6 flex items-center gap-3"
            : "flex items-center gap-2 text-sm text-emerald-700"
        }
        data-testid="newsletter-success"
      >
        <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0" />
        <p className="text-sm text-emerald-900 font-medium">
          Inscription confirmée. À très vite dans votre boîte mail !
        </p>
      </div>
    );
  }

  if (variant === "inline") {
    return (
      <form onSubmit={onSubmit} className="flex flex-col sm:flex-row gap-2 max-w-md" data-testid={`newsletter-form-${source}`}>
        <Input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Votre email"
          required
          disabled={loading}
          className="h-10"
          data-testid="newsletter-input"
        />
        <Button type="submit" disabled={loading} className="rounded-full h-10 px-5 shrink-0" data-testid="newsletter-submit">
          {loading ? "…" : "S'abonner"}
        </Button>
      </form>
    );
  }

  return (
    <div className="rounded-2xl bg-gradient-to-br from-slate-900 to-slate-700 text-white p-8" data-testid={`newsletter-card-${source}`}>
      <div className="flex flex-col md:flex-row md:items-center gap-6">
        <div className="flex-1">
          <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-amber-400/20 text-amber-300 text-[10px] font-bold uppercase tracking-wider">
            <Mail className="h-3 w-3" /> Newsletter mensuelle
          </div>
          <h3 className="mt-3 font-display text-xl md:text-2xl font-bold tracking-tight">
            Les meilleurs tutos spa, chaque mois dans votre boîte.
          </h3>
          <p className="mt-2 text-sm text-white/70">
            Une sélection des tutos les plus utiles votés par la communauté. Aucun spam, désabonnement en 1 clic.
          </p>
        </div>
        <form onSubmit={onSubmit} className="flex flex-col sm:flex-row gap-2 md:w-80 shrink-0" data-testid={`newsletter-form-${source}`}>
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Votre email"
            required
            disabled={loading}
            className="bg-white text-slate-900 h-11 border-0"
            data-testid="newsletter-input"
          />
          <Button type="submit" disabled={loading} className="rounded-full h-11 px-5 bg-primary hover:bg-primary/90 shrink-0" data-testid="newsletter-submit">
            {loading ? "…" : "M'abonner"}
          </Button>
        </form>
      </div>
    </div>
  );
}
